import { reflect } from '@effector/reflect';
import { CircleSlash2 } from 'lucide-react';
import { equals, not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockVoucherModel } from 'features/block-voucher/model';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES } from 'shared/api';
import { translated } from 'shared/lib';
import { ConfirmModalProps, createConfirmModal } from 'shared/ui';

export const createBlockVoucherModal = (model: BlockVoucherModel) => {
	const ConfirmModal = createConfirmModal(model);

	const BlockVoucherModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		return (
			<ConfirmModal
				ref={ref}
				title={t('block-voucher.title')}
				cancelButtonProps={{
					children: t('block-voucher.cancel'),
				}}
				submitButtonProps={{
					variant: 'outline',
					children: t('block-voucher.block'),
				}}
				{...props}
			>
				{t('block-voucher.description')}
			</ConfirmModal>
		);
	});

	const BlockVoucherModalButton = reflect({
		view: ConfirmModal.Button,
		bind: {
			'data-test-id': 'block-voucher-button',
			variant: 'outline',
			disabled: not(equals(viewerModel.$role, USER_ROLES.god)),
			leftSection: <CircleSlash2 />,
			children: translated('block-voucher.block'),
		},
	});

	const BlockVoucherModalActionIcon = reflect({
		view: ConfirmModal.ActionIcon,
		bind: {
			'data-test-id': 'block-voucher-action-icon',
			variant: 'ghost-secondary',
			disabled: not(equals(viewerModel.$role, USER_ROLES.god)),
			children: <CircleSlash2 />,
		},
	});

	return Object.assign(BlockVoucherModal, {
		Button: BlockVoucherModalButton,
		ActionIcon: BlockVoucherModalActionIcon,
	});
};
